import React, { useEffect, useState } from "react";

import { getSsoStatus, ssoLogin, ssoLogout, getSsoUserLabel, isSsoAuthenticated } from "../auth.js";

// Legacy local login, kept as the SSO-off fallback (and for dev).
// Credentials are hardcoded per Stefan's request.
// No account creation is allowed.
const VALID_USERNAME = "adgentek-user";
const VALID_PASSWORD = "xyxxoq-vYsgos-mykco6";

const STORAGE_KEY = "longship.auth";

export function isAuthenticated() {
  if (isSsoAuthenticated()) return true;
  try {
    return sessionStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

export function getCurrentUser() {
  if (isSsoAuthenticated()) return getSsoUserLabel();
  try {
    return sessionStorage.getItem("longship.user") || "adgentek-user";
  } catch {
    return "adgentek-user";
  }
}

export function logout() {
  if (isSsoAuthenticated()) {
    // Full SSO sign-out: clears the Keycloak session (SSO cookie) and lands
    // back on the app root, which re-runs the check-sso init.
    ssoLogout();
    return;
  }
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem("longship.user");
  } catch {
    // ignore
  }
  window.location.reload();
}

/** Legacy username/password form (SSO off). */
function LegacyLoginForm({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);

  function handleSubmit(e) {
    e.preventDefault();
    if (username === VALID_USERNAME && password === VALID_PASSWORD) {
      try {
        sessionStorage.setItem(STORAGE_KEY, "true");
        sessionStorage.setItem("longship.user", username);
      } catch {
        // ignore
      }
      onLogin();
    } else {
      setError("Invalid username or password.");
    }
  }

  return (
    <form onSubmit={handleSubmit} className="login-form">
      <label className="login-field">
        <span>Username</span>
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="username"
          autoFocus
          required
        />
      </label>
      <label className="login-field">
        <span>Password</span>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
        />
      </label>
      <button type="submit" className="btn btn-primary login-submit">
        Sign In
      </button>
    </form>
  );
}

/**
 * Login gate. SSO-first: the API's /auth/sso-config decides which UI shows.
 *   - SSO enabled  → "Sign in with Adgentek SSO" button (Keycloak redirect)
 *   - SSO disabled → legacy local form, unchanged
 * An active Keycloak session (check-sso) signs in silently on load.
 */
export default function LoginPage({ onLogin }) {
  const [sso, setSso] = useState(null); // null = checking, {enabled, authenticated}
  const [signingIn, setSigningIn] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getSsoStatus().then((status) => {
      if (cancelled) return;
      setSso(status);
      if (status.authenticated) onLogin();
    });
    return () => {
      cancelled = true;
    };
  }, [onLogin]);

  async function handleSsoSignIn() {
    setSigningIn(true);
    try {
      await ssoLogin(); // redirects to Keycloak; no return on success
    } finally {
      setSigningIn(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">
          <img src="/logo.svg" alt="Longship" className="login-logo-mark" />
          <span>Longship</span>
        </div>
        <h1 className="login-title">Sign In</h1>
        <p className="login-subtitle">IO Ingestion &amp; Campaign Generation Platform</p>

        {sso === null && <p className="login-subtitle">Checking sign-in options…</p>}

        {sso !== null && sso.enabled && !sso.authenticated && (
          <>
            <button
              type="button"
              className="btn btn-primary login-submit"
              onClick={handleSsoSignIn}
              disabled={signingIn}
            >
              {signingIn ? "Redirecting…" : "Sign in with Adgentek SSO"}
            </button>
            <p className="login-subtitle">
              Adgentek account sign-in (Keycloak). Use your @adgentek.ai identity.
            </p>
          </>
        )}

        {sso !== null && !sso.enabled && (
          <LegacyLoginForm onLogin={onLogin} />
        )}
      </div>
    </div>
  );
}
